import {
  KEEP_NUMBER_OPTION__KEEP,
  KEEP_NUMBER_OPTION__NEW,
  type NumberConfigState,
} from '~/models/checkout/checkout_store/NumberConfigCheckoutState.model';
import type { NumberSelectionModel } from '~/models/shopping_cart/ShoppingCart.api.model';
import type { ShoppingCartItemModel } from '~/models/shopping_cart/ShoppingCartItem.api.model';
import { Category } from '~/models/Category.models';

export const createDefaultNumberConfigState = (): NumberConfigState => ({
  bundleId: -1,
  cartItemId: -1,
  keepNumberInputValue: '',
  keepNumberCodeSent: false,
  keepNumberCodeInputValue: '',
  keepNumberCodeValidated: false,
  keepNumberShowError: false,
  keepNumberOption: KEEP_NUMBER_OPTION__KEEP,
  keepNumberInputFieldId: '',
  ownerSocialSecurityNumber: undefined,
  hiddenNumber: true,
  esim: false,
  keepNumberDevPortingCode: '',
  parentMsisdn: undefined,
});

export type NumberConfigByIds = {
  [bundleIdAndCartItemId: string]: NumberConfigState;
};

export const createNumberSelectionMapKey = (
  bundleId: number,
  cartItemId: number,
) => {
  return `${bundleId}_${cartItemId}`;
};

export const createMap = (
  numberSelections: NumberSelectionModel[] = [],
  cartItems: ShoppingCartItemModel[] = [],
) => {
  if (!numberSelections) {
    return {};
  }

  const newNumberConfigByBundleIdMap: NumberConfigByIds = {};
  const defaultNumberConfigState = createDefaultNumberConfigState();

  numberSelections.forEach((numberSelection) => {
    // (jan2025) Take the cart item of the "outer" cart item.
    // For bundles of phone+subscription, there is an inner item called
    // BundlePlanItem on the cart item with its own id.
    // This inner id is what we get in the NumberSelection response, but
    // we decided it's the outer main id of the whole cart item we want to
    // post when placing the order (jonathan+samuel).
    // Single subscriptions and family connections don't have this inner BundlePlanItem object.
    // This code is unnecessary but harmless if we change the response of
    // the NumberSelection object of the get shopping cart request to reflect this.
    const cartItem = cartItems.find(
      (fetchedCartItem) =>
        fetchedCartItem.Id === numberSelection.CartItemId ||
        fetchedCartItem?.BundlePlanItem?.Id === numberSelection.CartItemId,
    );

    if (!cartItem) {
      return;
    }

    const id = cartItem.BundlePlanItem?.Id ?? cartItem.Id;

    const key = createNumberSelectionMapKey(numberSelection.BundleId, id);

    //if category is mobile broadband, new number is the only allowed option
    newNumberConfigByBundleIdMap[key] = {
      bundleId: numberSelection.BundleId,
      cartItemId: id,
      keepNumberCodeInputValue:
        defaultNumberConfigState.keepNumberCodeInputValue,
      keepNumberCodeSent: defaultNumberConfigState.keepNumberCodeSent,
      keepNumberCodeValidated: defaultNumberConfigState.keepNumberCodeValidated,
      keepNumberInputValue: defaultNumberConfigState.keepNumberInputValue,
      keepNumberOption:
        cartItem.Category === Category.mobileBroadBand
          ? KEEP_NUMBER_OPTION__NEW
          : defaultNumberConfigState.keepNumberOption,
      keepNumberShowError: defaultNumberConfigState.keepNumberShowError,
      keepNumberDevPortingCode:
        defaultNumberConfigState.keepNumberDevPortingCode,
      ownerSocialSecurityNumber:
        defaultNumberConfigState.ownerSocialSecurityNumber,
      hiddenNumber: defaultNumberConfigState.hiddenNumber,
      esim: defaultNumberConfigState.esim,
      parentMsisdn:
        cartItem.BundlePlanItem?.ParentMsisdn ?? cartItem.ParentMsisdn,
    };
  });

  return newNumberConfigByBundleIdMap;
};
